import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Link,
  AppBar,
  Toolbar,
  Grow,
  Fade,
  Slide,
  Zoom,
  Alert,
  CircularProgress,
} from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store/index'
import { setLoading, setError } from '@store/slices/authSlice'
import { addNotification } from '@store/slices/appSlice'
import api from '@services/api'
import { createValidationRules, validateForm } from '@utils/validation'

/**
 * Determines the user role based on the referrer route
 */
const getUserRoleFromReferrer = (
  locationState: any
): 'patient' | 'therapist' => {
  // Check location state first (most reliable)
  if (locationState?.from === '/patient-login') {
    return 'patient'
  }

  if (locationState?.from === '/provider-login') {
    return 'therapist'
  }

  // Fallback to document referrer
  const referrer = document.referrer
  if (referrer.includes('/patient-login')) {
    return 'patient'
  }

  if (referrer.includes('/provider-login')) {
    return 'therapist'
  }

  // Default to patient if cannot determine
  return 'patient'
}

/**
 * Determines the role-based color based on the referrer route
 */
const getRoleColorFromReferrer = (locationState: any): string => {
  const role = getUserRoleFromReferrer(locationState)
  return role === 'patient' ? '#3BAE78' : '#2E7DAF'
}

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { error, isLoading } = useAppSelector(state => state.auth)

  const [email, setEmail] = useState('')
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [success, setSuccess] = useState(false)
  const [primaryColor, setPrimaryColor] = useState('#3BAE78')
  const [userRole, setUserRole] = useState<'patient' | 'therapist'>('patient')

  useEffect(() => {
    // Determine role and color based on where user came from
    const role = getUserRoleFromReferrer(location.state)
    const color = getRoleColorFromReferrer(location.state)
    setUserRole(role)
    setPrimaryColor(color)
  }, [location.state])

  const validationRules = {
    email: [
      createValidationRules.required('Email is required'),
      createValidationRules.email(),
    ],
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
    // Clear field error when user starts typing
    if (formErrors.email) {
      setFormErrors(prev => ({
        ...prev,
        email: '',
      }))
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    // Validate form
    const errors = validateForm({ email }, validationRules)
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    dispatch(setLoading(true))
    dispatch(setError(null))
    setSuccess(false)

    try {
      const endpoint =
        userRole === 'therapist'
          ? '/therapist/forgot-password'
          : '/patient/forgot-password'

      await api.post(endpoint, {
        email: email,
      })

      setSuccess(true)
      dispatch(
        addNotification({
          type: 'success',
          title: 'Email Sent',
          message: 'Please check your email for password reset instructions.',
        })
      )
    } catch (error: any) {
      console.error('Forgot password failed:', error)
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to send reset email. Please try again.'
      dispatch(setError(errorMessage))
    } finally {
      dispatch(setLoading(false))
    }
  }

  const handleBackToLogin = () => {
    // Determine which login page to go back to based on color
    if (primaryColor === '#3BAE78') {
      navigate('/patient-login')
    } else {
      navigate('/provider-login')
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Fade in timeout={800}>
        <AppBar
          position="static"
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            height: '80px',
            flexShrink: 0,
          }}
        >
          <Toolbar
            sx={{ justifyContent: 'space-between', py: 1, height: '100%' }}
          >
            <Slide direction="right" in timeout={1000}>
              <Box
                component="img"
                src="/logo.svg"
                alt="PillarThera Logo"
                sx={{
                  height: { xs: 32, sm: 60 },
                  width: 'auto',
                  cursor: 'pointer',
                  transition: 'opacity 0.3s ease',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
                onClick={() => navigate('/')}
              />
            </Slide>
          </Toolbar>
        </AppBar>
      </Fade>

      {/* Main Content */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Left Section - Image */}
        <Zoom in timeout={1200} style={{ transitionDelay: '300ms' }}>
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              display: { xs: 'none', md: 'block' },
              overflow: 'hidden',
              bgcolor: 'grey.50',
              paddingLeft: '3rem',
              paddingTop: '1rem',
            }}
          >
            <Box
              component="img"
              src="/Group_6.png"
              alt="Forgot password illustration"
              sx={{
                width: '90%',
                height: '90%',
                objectFit: 'cover',
                objectPosition: 'center',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                },
              }}
            />
          </Box>
        </Zoom>

        {/* Right Section - Forgot Password Form */}
        <Slide
          direction="left"
          in
          timeout={1000}
          style={{ transitionDelay: '500ms' }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              px: { xs: 3, sm: 4, md: 6, lg: 8 },
              py: { xs: 4, sm: 6 },
              bgcolor: 'grey.50',
            }}
          >
            {/* Forgot Password Form Card */}
            <Grow in timeout={1200} style={{ transitionDelay: '700ms' }}>
              <Paper
                elevation={2}
                sx={{
                  p: { xs: 4, sm: 5, md: 6 },
                  borderRadius: 4,
                  width: '100%',
                  maxWidth: { xs: '100%', sm: '450px', md: '500px' },
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {/* Header */}
                <Fade in timeout={1000} style={{ transitionDelay: '900ms' }}>
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        color: 'grey.800',
                        mb: 1,
                        fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                      }}
                    >
                      Forgot Password?
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'grey.600',
                        fontWeight: 400,
                        fontSize: { xs: '1rem', sm: '1.125rem' },
                      }}
                    >
                      {success
                        ? 'Check your email for reset instructions'
                        : 'Enter your email address and we will send you instructions to reset your password'}
                    </Typography>
                  </Box>
                </Fade>

                {/* Success Display */}
                {success && (
                  <Fade in timeout={800} style={{ transitionDelay: '1000ms' }}>
                    <Alert severity="success" sx={{ mb: 3 }}>
                      Password reset email has been sent. Please check your
                      inbox and follow the instructions to reset your password.
                    </Alert>
                  </Fade>
                )}

                {/* Error Display */}
                {error && !success && (
                  <Fade in timeout={800} style={{ transitionDelay: '1000ms' }}>
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  </Fade>
                )}

                {/* Forgot Password Form */}
                {!success && (
                  <Box component="form" onSubmit={handleSubmit}>
                    {/* Email Field */}
                    <Fade
                      in
                      timeout={800}
                      style={{ transitionDelay: '1100ms' }}
                    >
                      <Box sx={{ mb: 4 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'grey.700',
                            mb: 1,
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          Email
                        </Typography>
                        <TextField
                          fullWidth
                          type="email"
                          value={email}
                          onChange={handleInputChange}
                          placeholder="Enter your email address"
                          required
                          error={!!formErrors.email}
                          helperText={formErrors.email}
                          disabled={isLoading}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'white',
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              '& fieldset': {
                                borderColor: 'grey.300',
                              },
                              '&:hover fieldset': {
                                borderColor: 'grey.400',
                                transform: 'scale(1.02)',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                                borderWidth: 2,
                              },
                            },
                            '& .MuiInputBase-input': {
                              py: 1.5,
                              fontSize: '0.875rem',
                            },
                          }}
                        />
                      </Box>
                    </Fade>

                    {/* Submit Button */}
                    <Fade
                      in
                      timeout={800}
                      style={{ transitionDelay: '1300ms' }}
                    >
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={isLoading}
                        sx={{
                          bgcolor: primaryColor,
                          py: 1.5,
                          fontSize: '1rem',
                          fontWeight: 600,
                          borderRadius: 2,
                          mb: 3,
                          textTransform: 'none',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor:
                              primaryColor === '#3BAE78'
                                ? '#2d8c5f'
                                : '#2563a0',
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${primaryColor}40`,
                          },
                          '&:disabled': {
                            bgcolor: 'grey.300',
                            color: 'grey.500',
                          },
                        }}
                      >
                        {isLoading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          'Send Reset Instructions'
                        )}
                      </Button>
                    </Fade>
                  </Box>
                )}

                {/* Back to Login Link */}
                <Fade in timeout={800} style={{ transitionDelay: '1500ms' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'grey.600',
                        fontSize: '0.875rem',
                      }}
                    >
                      Remember your password?{' '}
                      <Link
                        component="button"
                        type="button"
                        onClick={handleBackToLogin}
                        sx={{
                          color: primaryColor,
                          textDecoration: 'none',
                          fontWeight: 600,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            textDecoration: 'underline',
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        Back to Login
                      </Link>
                    </Typography>
                  </Box>
                </Fade>
              </Paper>
            </Grow>
          </Box>
        </Slide>
      </Box>
    </Box>
  )
}

export default ForgotPasswordPage
